import React, { useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Lock, ArrowLeft, GraduationCap, Sparkles } from 'lucide-react';
import { useForm } from 'react-hook-form';
import authService from '../../services/api/auth';
import { useToast } from '../../components/common/Toast';

const ResetPassword = () => {
  const { token } = useParams();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [done, setDone] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const newPassword = watch('newPassword');

  if (!token) {
    return <Navigate to="/forgot-password" replace />;
  }

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      await authService.resetPassword({ token, newPassword: data.newPassword });
      toast.success('تم تحديث كلمة المرور بنجاح، يمكنك الآن تسجيل الدخول.');
      setDone(true);
    } catch (error) {
      const msg = error.response?.data?.error || 'فشل في إعادة تعيين كلمة المرور، حاول مرة أخرى.';
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50/60 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      <div className="absolute top-[-10%] right-[-10%] w-[40%] h-[40%] bg-primary-100/40 rounded-full blur-[120px] -z-10 animate-pulse" />
      <div className="absolute bottom-[-10%] left-[-10%] w-[30%] h-[40%] bg-blue-100/40 rounded-full blur-[100px] -z-10" />

      <div className="max-w-md mx-auto w-full space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
        {/* Branding Header */}
        <div className="text-center space-y-4">
          <Link to="/" className="inline-flex items-center gap-3 group">
            <div className="bg-primary-600 p-4 rounded-[1.5rem] shadow-xl shadow-primary-200 transform group-hover:rotate-12 transition-all duration-500">
              <GraduationCap className="h-8 w-8 text-white" />
            </div>
            <div className="text-right">
              <h1 className="text-3xl font-black text-gray-900 tracking-tight leading-none">مـنـصـتـي</h1>
              <p className="text-primary-600 text-xs font-black uppercase tracking-[0.2em] mt-1">
                تعيين كلمة مرور جديدة
              </p>
            </div>
          </Link>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-gray-200/50 p-8 lg:p-10 border border-gray-100">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">
                كلمة المرور الجديدة
              </label>
              <div className="relative group">
                <Lock className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
                <input
                  {...register('newPassword', {
                    required: 'كلمة المرور الجديدة مطلوبة',
                    minLength: { value: 6, message: 'كلمة المرور يجب أن تكون 6 أحرف على الأقل' },
                  })}
                  type="password"
                  autoComplete="new-password"
                  placeholder="••••••••"
                  className={`
                    w-full pr-12 pl-4 py-4 bg-gray-50 border rounded-2xl focus:outline-none focus:ring-4 focus:ring-primary-500/10 focus:bg-white transition-all font-bold
                    ${errors.newPassword ? 'border-red-300' : 'border-gray-100 focus:border-primary-500'}
                  `}
                />
              </div>
              {errors.newPassword && (
                <p className="mt-1 mr-2 text-[10px] text-red-500 font-black flex items-center gap-1">
                  <Sparkles size={10} /> {errors.newPassword.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">
                تأكيد كلمة المرور الجديدة
              </label>
              <div className="relative group">
                <Lock className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
                <input
                  {...register('confirmPassword', {
                    required: 'تأكيد كلمة المرور مطلوب',
                    validate: (value) => value === newPassword || 'كلمتا المرور غير متطابقتين',
                  })}
                  type="password"
                  autoComplete="new-password"
                  placeholder="••••••••"
                  className={`
                    w-full pr-12 pl-4 py-4 bg-gray-50 border rounded-2xl focus:outline-none focus:ring-4 focus:ring-primary-500/10 focus:bg-white transition-all font-bold
                    ${errors.confirmPassword ? 'border-red-300' : 'border-gray-100 focus:border-primary-500'}
                  `}
                />
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 mr-2 text-[10px] text-red-500 font-black flex items-center gap-1">
                  <Sparkles size={10} /> {errors.confirmPassword.message}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 bg-primary-600 text-white py-4 rounded-2xl font-black shadow-lg shadow-primary-200 hover:bg-primary-700 transition-all active:scale-95 disabled:opacity-60"
            >
              {isLoading ? 'جارٍ تحديث كلمة المرور...' : 'حفظ كلمة المرور الجديدة'}
            </button>
          </form>

          <div className="mt-6 flex items-center justify-between text-xs font-black text-gray-500">
            <Link to="/login" className="inline-flex items-center gap-1 hover:text-primary-600 transition-colors">
              <ArrowLeft size={14} /> العودة لتسجيل الدخول
            </Link>
            {!done && (
              <Link to="/forgot-password" className="hover:text-primary-600 transition-colors">
                لم يصلك البريد؟ إعادة الإرسال
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;

